import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '$store';
import useAppSelector from '~hooks/useAppSelector';
import useAssets from '~hooks/useAssets';
import usePermissions from '~hooks/usePermissions';
import useMediaLibraryUserPreferences from '~modules/media-library/context/useMediaLibraryUserPreferences';
import { onEdiMultiAssets } from '~modules/media-library/controller/thunks/contribute';
import { type Asset, type AssetId } from '~modules/media-library/model/Asset';
import { type AssetActions } from '~modules/media-library/model/AssetActions';
import { isAssetCollection, isAssetVideo, isCanvaTemplate } from '~modules/media-library/model/AssetFactory';
import { ViewVariant } from '~modules/media-library/model/ViewVariant';
import { List } from '~modules/media-library/view/List/List';
import { Teaser } from '~modules/media-library/view/Teaser/Teaser';
import { selectAddonsIsWeb2PrintMaintenance } from '~store/selectors';

export type ResultListProps = XOR<{ assets: Asset[] }, { assetIds: AssetId[] }> & {
  assetCount: number;
  onLoadMore: () => void;
  AssetActions: AssetActions;
  viewVariant?: ViewVariant;
};

export function ResultList({
  assets,
  assetIds,
  assetCount,
  onLoadMore,
  viewVariant = ViewVariant.DEFAULT,
  AssetActions,
}: ResultListProps) {
  const { getAssetById } = useAssets();
  const dispatch = useDispatch();

  const isWeb2PrintMaintenance = useAppSelector(selectAddonsIsWeb2PrintMaintenance);
  const { hasPassMaintenanceModePermission } = usePermissions();
  const data = useSelector((store: RootState) => store.contribute);
  const { selectedTabFilterId } = useMediaLibraryUserPreferences();
  assets = (assets || assetIds?.map(getAssetById) || []).filter(Boolean);
  return (
    <div className="ml--result-list px-3">
      {selectedTabFilterId === 'WorkflowState~~~draft' && data?.selectedAssetIds?.length > 0 && (
        <div className="mx-auto max-w-screen-xl">
          <button
            className="ml--button ml--button-default appearance-none outline-none overflow-hidden inline-flex m-0 text-black font-sans leading-none rounded items-center overflow-hidden select-none transition whitespace-nowrap border-silver ml--select-button ml--filter-button  hover:border-black cursor-pointer border border-solid py-2 px-3 bg-white h-8 text-sm"
            onClick={() => dispatch(onEdiMultiAssets())}
          >
            Edit Selected
          </button>
        </div>
      )}
      <List count={assetCount} onLoadMore={onLoadMore} viewVariant={viewVariant}>
        {assets.map(asset => (
          <Teaser
            key={asset.id}
            asset={asset}
            variant={viewVariant}
            AssetActions={{
              ...AssetActions,
              onCollection: isAssetCollection(asset) ? AssetActions.onCollection : undefined,
              onAddToMyfiles: asset.actions.myFiles ? AssetActions.onAddToMyfiles : undefined,
              onCopyId: asset.actions.copyId ? AssetActions.onCopyId : undefined,
              onCrop: asset.actions.crop ? AssetActions.onCrop : undefined,
              onDownload: asset.actions.download ? AssetActions.onDownload : undefined,
              onToggleFavourite: asset.actions.favourites ? AssetActions.onToggleFavourite : undefined,
              onUseWeb2Print:
                asset.actions.web2Print && (!isWeb2PrintMaintenance || hasPassMaintenanceModePermission)
                  ? AssetActions.onUseWeb2Print
                  : undefined,
              // all user can see the button and in the modal it will show a message if user has no permissions
              onUseCanva: isCanvaTemplate(asset) ? AssetActions.onUseCanva : undefined,
              onPlay: isAssetVideo(asset) && Boolean(asset.attributes.videoPreview) ? AssetActions.onPlay : undefined,
              onUseRaiseRequestAssets: asset.actions.raiseRequest ? AssetActions.onUseRaiseRequestAssets : undefined,
              openContributionWizard: asset.actions.edit ? AssetActions.openContributionWizard : undefined,
              onConfirmDelete: asset.actions.delete ? AssetActions.onConfirmDelete : undefined,
              onApprovalForm: asset.actions.approve ? AssetActions.onApprovalForm : undefined,
              onRestartContribution: asset.actions.restartContribution ? AssetActions.onRestartContribution : undefined,
              onJsmContactForm: asset.actions.jsmContactForm ? AssetActions.onJsmContactForm : undefined,
            }}
          />
        ))}
      </List>
    </div>
  );
}
